import {
  GroupInboxConfigDocument,
  GroupInboxConfigEntry,
} from "../../../../../../shared/api/services/groupInbox/groupInboxConfigTypes";
import type { GiConfiguration } from "../hooks/useGiConfiguration";

/**
 * Reads a string field from a Group Inbox config document.
 *
 * @param value - Unknown field value from the API document
 * @returns The string when present, otherwise undefined
 */
const readOptionalString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

/**
 * Reads a number field from a Group Inbox config document.
 *
 * @param value - Unknown field value from the API document
 * @returns The number when present, otherwise undefined
 */
const readOptionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

/**
 * Maps one `{viewTabVersion: configDoc}` API entry onto the dashboard GI model.
 *
 * The object key is the view-tab document id (`GiConfiguration.id`). Fields on
 * the stored version document populate the remaining UI properties.
 *
 * @param viewTabVersion - View-tab document id used as the configuration id
 * @param document - Latest stored version document for that view tab
 * @param orgId - Organization used as a fallback when the document omits `org_id`
 * @returns Dashboard GI configuration for a single view tab
 */
const mapGroupInboxConfigDocument = (
  viewTabVersion: string,
  document: GroupInboxConfigDocument,
  orgId: string,
): GiConfiguration => ({
  id: viewTabVersion,
  view_tab: readOptionalString(document.view_tab),
  version: readOptionalString(document.version) ?? viewTabVersion,
  status: readOptionalString(document.status),
  org_id: readOptionalString(document.org_id) ?? orgId,
  created_at: readOptionalString(document.created_at),
  created_by: readOptionalString(document.created_by),
  last_updated_by: readOptionalString(document.last_updated_by),
  last_updated: readOptionalString(document.last_updated),
  config: Array.isArray(document.config) ? document.config : undefined,
  num_orders: readOptionalNumber(document.num_orders),
});

/**
 * Maps the Group Inbox configs API list into dashboard `GiConfiguration` rows.
 *
 * @param configs - Version-keyed config documents from the backend
 * @param orgId - Organization that owns the configurations
 * @returns One GI configuration per non-empty API entry
 */
export const mapGroupInboxConfigsToGiConfigurations = (
  configs: GroupInboxConfigEntry[],
  orgId: string,
): GiConfiguration[] =>
  configs.flatMap((entry) => {
    const viewTabVersion = Object.keys(entry)[0];
    if (!viewTabVersion) {
      return [];
    }
    return [
      mapGroupInboxConfigDocument(
        viewTabVersion,
        entry[viewTabVersion] ?? {},
        orgId,
      ),
    ];
  });
